import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { getSiteSettings } from '../api/client';

const SettingsContext = createContext();

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within SettingsProvider');
  }
  return context;
};

export const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({
    eventDate: '2026-09-22T09:00:00+07:00',
    heroTitle: 'ASEAN Business Forum',
    heroSubtitle: 'A global platform connecting halal trade, tourism, lifestyle, and innovation.',
    heroLocation: 'Batam & Bintan, Indonesia',
    heroYear: '2026',
    heroFeature: 'B2B Matchmaking',
    stats: {
      exhibitors: '300+',
      countries: '50+',
      delegations: '150+'
    },
    colors: {
      primary: '#0E7A5F',
      primaryHover: '#0A5F4A'
    }
  });
  const [loading, setLoading] = useState(true);
  const [fetchFailed, setFetchFailed] = useState(false);

  const fetchSettings = useCallback(async (retryCount = 0) => {
    try {
      const data = await getSiteSettings();
      setSettings(data);
      setFetchFailed(false);
    } catch (error) {
      if (retryCount < 2) {
        setTimeout(() => fetchSettings(retryCount + 1), 2000 * (retryCount + 1));
      } else {
        setFetchFailed(true);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    
    const interval = setInterval(() => {
      if (!window.location.pathname.startsWith('/admin')) {
        fetchSettings();
      }
    }, 30000);
    
    return () => clearInterval(interval);
  }, [fetchSettings]);

  const refreshSettings = useCallback(async () => {
    try {
      const data = await getSiteSettings();
      setSettings(data);
      setFetchFailed(false);
    } catch {
      // Silent fail — settings remain at last known good state
    }
  }, []);

  const value = useMemo(() => ({ settings, loading, fetchFailed, refreshSettings }), [settings, loading, fetchFailed, refreshSettings]);

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
